/**
 * Format a remaining-seconds count as "Xm Ys" for TTL countdowns.
 * Negative/zero reads as expired rather than a nonsense negative duration.
 */
export function formatDurationShort(totalSeconds: number): string {
  if (totalSeconds <= 0) return 'expired'
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`
}

/**
 * Format a file size in bytes to a human-readable string
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * The zone every REPORTED date is reckoned in, mirroring the API's
 * `REPORTING_TIME_ZONE`.
 *
 * Analytics buckets HubSpot-sourced events, and the HubSpot portal's own clock
 * is Mountain — so a case's "deadline day" is its Denver day, on the page, on
 * the ticket and in the medians alike.
 */
export const REPORTING_TIME_ZONE = 'America/Denver'

/**
 * A reported date, always in {@link REPORTING_TIME_ZONE}.
 *
 * **Use this, not {@link formatDate}, for anything the API computed in the
 * reporting zone** — deadlines, safe starts, projected ready dates, stage
 * entries. `formatDate` renders in the VIEWER's timezone, so an evening
 * meeting shows one day to someone in Denver and another to someone in New
 * York, and neither necessarily matches the date written on the HubSpot
 * ticket. Tyler hit exactly that kind of split on ticket 48655730343.
 *
 * `formatDate` is left alone deliberately: 178 call sites across the app, many
 * of them client-facing timestamps where the viewer's own clock is the right
 * one. This is the narrow one for figures the backend already decided.
 */
export function formatReportingDate(
  dateStr: string | null | undefined,
): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      timeZone: REPORTING_TIME_ZONE,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

/**
 * Format an ISO date string as "Mar 26, 2026".
 * Returns "—" for null/undefined; falls back to the raw string on parse error.
 *
 * For a timestamp. Do NOT use on a date-only value ("2026-08-01") — see
 * formatDateOnly for why.
 */
/**
 * A closed date range on one line: "Jul 15 - Aug 24, 2026" within a year,
 * "Dec 20, 2025 - Jan 5, 2026" across years (sc-1396, Tyler, Oct 2, 2026).
 */
export function formatDateRange(fromIso: string, toIso: string): string {
  const from = new Date(fromIso)
  const to = new Date(toIso)
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return `${formatDate(fromIso)} - ${formatDate(toIso)}`
  }
  if (from.getFullYear() !== to.getFullYear()) {
    return `${formatDate(fromIso)} - ${formatDate(toIso)}`
  }
  const fromText = from.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
  return `${fromText} - ${formatDate(toIso)}`
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

/**
 * Format a date-only value ("2026-08-01") as "August 1, 2026".
 *
 * `new Date('2026-08-01')` parses as UTC midnight, so formatting it in the
 * viewer's zone renders the previous day anywhere west of UTC — a contract that
 * starts Aug 1 shows as Jul 31 in Denver. Anchoring the format to UTC keeps the
 * calendar date the database actually stored.
 *
 * Use for `@db.Date` columns (term dates, dates of birth, presentation dates);
 * use formatDate for full timestamps, which should stay in the viewer's zone.
 */
export function formatDateOnly(dateStr: string | null | undefined): string {
  return formatUtcDate(dateStr, 'long')
}

/**
 * Format a date-only value ("2026-08-01") as "Aug 1, 2026".
 *
 * Same UTC anchoring as {@link formatDateOnly}, and for the same reason — see
 * its comment. Use this one wherever the surrounding dates are abbreviated, so
 * a card does not mix "Aug 1, 2026" with "August 1, 2026".
 */
export function formatDateOnlyShort(
  dateStr: string | null | undefined,
): string {
  return formatUtcDate(dateStr, 'short')
}

function formatUtcDate(
  dateStr: string | null | undefined,
  month: 'long' | 'short',
): string {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  // toLocaleDateString does not throw on an unparseable input, it returns the
  // literal "Invalid Date" — so the check has to be explicit.
  if (Number.isNaN(date.getTime())) return dateStr
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month,
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format an ISO date string as "Mar 26, 2026, 3:45 PM" in the viewer's local
 * time zone. Returns "—" for null/undefined; falls back to the raw string on
 * parse error.
 */
export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

/**
 * Format an ISO date string as "Mar 26, 2026, 3:45:12.345 PM" — seconds AND
 * milliseconds included. `formatDateTime` deliberately rounds to the minute,
 * which is the right call for everyday timestamps but hides ordering for an
 * audit trail where two events can land under a second apart (e.g. the
 * billing audit drawer's webhook list, where Stripe delivery order — not
 * guaranteed — is the whole point of the section). Returns "—" for
 * null/undefined; falls back to the raw string on parse error.
 */
export function formatDateTimePrecise(
  dateStr: string | null | undefined,
): string {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return dateStr
  const base = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })
  const ms = String(date.getMilliseconds()).padStart(3, '0')
  // Splice milliseconds in right after the seconds field, before AM/PM.
  return base.replace(/(:\d{2})(\s?[AP]M)/, `$1.${ms}$2`)
}

/**
 * Format a number as USD with no decimals.
 * Returns "—" for null values.
 */
export function formatCurrencyNumber(amount: number | null): string {
  if (amount === null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
}

/**
 * sc-1205. Format a number as USD with an explicit sign — `+$120` / `-$45` /
 * `$0` — for a figure where the sign itself is the point, like a pro-rata
 * run's drift. Unlike `formatCurrencyNumber`, a positive value is NOT shown
 * bare: drift over target and drift under target must read as visibly
 * different signs, not just different numbers. Returns "—" for null.
 */
export function formatCurrencySigned(amount: number | null): string {
  if (amount === null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    signDisplay: 'exceptZero',
  }).format(amount)
}

/**
 * Format a number as USD keeping cents (2 decimals) — for per-unit values
 * like share prices, where whole-dollar rounding (`formatCurrencyNumber`)
 * would drop meaningful precision (e.g. $271.44 → $271). Returns "—" for null.
 */
export function formatCurrencyPrecise(amount: number | null): string {
  if (amount === null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Format a number as compact USD — "$50.5K", "$1.1M", "-$41K" — for figures
 * packed into narrow cells (step 1's candidate rows, a bar chart's labels).
 * Returns "—" for null.
 *
 * `maximumSignificantDigits` trades the one-decimal rule for a fixed number
 * of significant digits — the Escape Plan card's savings figures use 3, so
 * $1,240,000 reads "$1.24M" rather than "$1.2M".
 */
export function formatCurrencyCompact(
  amount: number | null,
  options?: { maximumSignificantDigits?: number },
): string {
  if (amount === null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    ...(options?.maximumSignificantDigits
      ? { maximumSignificantDigits: options.maximumSignificantDigits }
      : { maximumFractionDigits: 1 }),
  }).format(amount)
}

/**
 * Parse a free-typed currency string (e.g. "$50,000.25", "50000") back into
 * a plain number, stripping everything but digits/decimal point/minus sign.
 * Returns 0 for empty or unparseable input rather than NaN, since callers
 * feed this straight into numeric state (editable currency pills/inputs).
 */
export function parseCurrencyInput(value: string): number {
  const numeric = Number(value.replace(/[^0-9.-]+/g, ''))
  return Number.isFinite(numeric) ? numeric : 0
}

/**
 * Format a numeric string (e.g. HubSpot's deal `amount` property) as USD
 * with no decimals. Returns "—" for null/undefined/unparseable values.
 *
 * Distinct from the local `formatCurrency(value: number)` helpers scattered
 * around routes/tools — this one is specifically for string inputs from
 * external APIs (HubSpot, etc.).
 */
export function formatCurrencyString(
  amount: string | null | undefined,
): string {
  if (!amount) return '—'
  const num = parseFloat(amount)
  if (isNaN(num)) return amount
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(num)
}

/**
 * Format a day count — "12 days", "1 day". Returns "—" when there is no
 * figure, which is NOT the same as zero: a median with no samples behind it
 * has to read as an absence, or "0 days to approve" looks like an achievement.
 *
 * `compact` gives "12d" for a table cell, where the column header already
 * carries the unit and the row has no room to repeat it.
 *
 * Replaces three near-identical local `days()` helpers in the Analytics
 * console that disagreed on both the unit and the empty value.
 */
export function formatDays(
  value: number | null | undefined,
  options?: { compact?: boolean },
): string {
  if (value === null || value === undefined) return '—'
  if (options?.compact) return `${value}d`
  return `${value} ${value === 1 ? 'day' : 'days'}`
}

/**
 * "34 business days" / "1 business day" / "—" for a missing count. The
 * business-day sibling of {@link formatDays}; the unit is spelled out because
 * a bare "34 days" beside calendar-day figures reads as the wrong clock.
 */
export function formatBusinessDays(count: number | null | undefined): string {
  if (count === null || count === undefined) return '—'
  return `${count} business ${count === 1 ? 'day' : 'days'}`
}

const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
]

/** Spelled out for 1-12 (the house voice), digits past that. */
function weekCountWord(weeks: number): string {
  return weeks <= 12 ? NUMBER_WORDS[weeks] : String(weeks)
}

/**
 * A business-day count with its week equivalent as a comprehension aid:
 * "25 business days (five business weeks)" when it divides evenly by 5,
 * "34 business days (about seven business weeks)" otherwise, and just the day
 * count under 5. `null` in, `null` out — callers must branch, so a missing
 * number can never be papered over with a phrase.
 *
 * **Days first, weeks second, and the weeks are allowed to be approximate.**
 * The day count is the authority; the week phrase only helps a reader feel
 * its size. Interpolating a number into "five business weeks" is how the copy
 * went wrong at 34 days.
 */
export function formatBusinessDayWindow(days: number | null): string | null {
  if (days === null) return null
  const base = formatBusinessDays(days)
  if (days < 5) return base
  const weeks = Math.round(days / 5)
  const exact = days % 5 === 0
  const unit = weeks === 1 ? 'business week' : 'business weeks'
  return `${base} (${exact ? '' : 'about '}${weekCountWord(weeks)} ${unit})`
}

/**
 * Format a decimal hour count — "1 hr", "2.5 hrs", "0 hrs".
 *
 * Distinct from {@link formatDurationMs}, which takes milliseconds and picks
 * its own unit: this is for a figure that is already hours and must STAY in
 * hours, because the number beside it is money per hour (sc-1212 round 14's
 * billable review). Rendering 90 minutes as "1.5h" beside "$750" is readable;
 * rendering it as "90m" leaves the reader doing the division.
 *
 * At most one decimal, and a whole number keeps no trailing ".0" — a column of
 * "2 hrs / 2.5 hrs" reads cleaner than "2.0 hrs / 2.5 hrs".
 *
 * Returns "—" for null, which is NOT the same as zero: a meeting with no end
 * time in HubSpot has no measured length, and "0 hrs" would claim it took none.
 */
export function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const rounded = Math.round(value * 10) / 10
  return `${rounded} ${rounded === 1 ? 'hr' : 'hrs'}`
}

/**
 * Format a millisecond duration as human time — "42s", "18m", "3.1h",
 * "41.8d". Built for the Case Design stage-duration audit (sc-1212), where a
 * single stage can span anywhere from minutes to hundreds of days (Closed's
 * timer never stops), so one fixed unit would either round a same-day stage
 * to "0d" or print raw millisecond noise for one closed a year ago.
 *
 * Returns "—" for null. That is NOT the same as a real `0` (which prints
 * "0s") — `stageDurations`/`specialistTimeMs` being null means "never
 * synced," and a caller must branch on that before reaching for this, same
 * as `formatDays`.
 */
export function formatDurationMs(ms: number | null): string {
  if (ms === null) return '—'
  const clamped = Math.max(0, ms)
  const seconds = clamped / 1000
  const minutes = seconds / 60
  const hours = minutes / 60
  const days = hours / 24
  if (clamped < 60_000) return `${Math.round(seconds)}s`
  if (clamped < 3_600_000) return `${Math.round(minutes)}m`
  if (clamped < 86_400_000) return `${hours.toFixed(1)}h`
  return `${days.toFixed(1)}d`
}

/**
 * Format a 0–1 rate as a whole-number percentage — "42%". Returns "—" for
 * null, which a rate MUST be able to express (e.g. a RothRadar™ engagement
 * cohort with nothing scoreable yet) rather than a misleading "0%".
 */
export function formatPercent(rate: number | null): string {
  if (rate === null) return '—'
  return `${Math.round(rate * 100)}%`
}

/**
 * Detect the display content type from a MIME type or filename extension
 */
export function getFileContentType(
  contentType: string | null,
  filename: string,
): 'pdf' | 'image' | 'csv' | 'generic' {
  if (contentType) {
    if (contentType === 'application/pdf') return 'pdf'
    if (contentType.startsWith('image/')) return 'image'
    if (contentType === 'text/csv' || contentType === 'application/csv')
      return 'csv'
  }

  const ext = filename.split('.').pop()?.toLowerCase()
  if (ext === 'pdf') return 'pdf'
  if (ext === 'csv') return 'csv'
  if (ext === 'png' || ext === 'jpg' || ext === 'jpeg' || ext === 'webp')
    return 'image'

  return 'generic'
}

// ============================================================================
// Trash countdown (sc-1352)
// ============================================================================

/**
 * Whole days left until `purgeAt`, never negative, or null when the input is
 * unparseable. Computed on the client from the scheduled purge time so a
 * long-open Trash view keeps counting down (see useNowTick) instead of
 * trusting the `daysLeft` the API computed at fetch time.
 */
export function getDaysLeft(
  purgeAt: string,
  now: number = Date.now(),
): number | null {
  const purgeMs = new Date(purgeAt).getTime()
  if (Number.isNaN(purgeMs)) return null
  const msPerDay = 24 * 60 * 60 * 1000
  return Math.max(0, Math.ceil((purgeMs - now) / msPerDay))
}

/**
 * Format the time left before a trashed item is purged: "Deleting today",
 * "1 day left", "n days left". Falls back to the same dash placeholder the
 * other formatters use for an unparseable date.
 */
export function formatDaysLeft(
  purgeAt: string,
  now: number = Date.now(),
): string {
  const days = getDaysLeft(purgeAt, now)
  if (days === null) return formatDate(null)
  if (days === 0) return 'Deleting today'
  if (days === 1) return '1 day left'
  return `${days} days left`
}

/**
 * How many cases a median rests on, in words.
 *
 * Words rather than `n = 2`: statistical notation reads as noise on a
 * management table. The information still matters — a median over one case is
 * not comparable to one over forty — so it is kept, just said plainly.
 */
/**
 * How many Plan Delivery verdicts are not due yet, in words.
 *
 * Shared by the table cell, the mobile caption, the panel and the person detail
 * page so one wording cannot drift into four — the same reason
 * {@link formatSampleCaption} exists. Words rather than a bare number, because
 * "3" beside a failure count reads as three more failures.
 */
export function formatPendingEpCaption(pending: number): string {
  return pending === 1
    ? '1 first meeting is still upcoming, so no verdict is due yet'
    : `${pending} first meetings are still upcoming, so no verdict is due yet`
}

/**
 * Coverage for a Plan Work median (sc-1212, extended round 3 for the
 * first-pass/rework split). `sampleSize` counts cases that met `phase`, not
 * every case in the window, so this says so rather than implying N is the
 * full population.
 *
 * `phase` defaults to the original combined-median wording so every caller
 * before the round-3 split keeps reading exactly as before. The rework
 * caption passes its own phase ("took at least one change request") — that
 * sample is a different population from "reached the plan phase" (a case
 * can reach the phase and never need a change), so reusing the default
 * wording there would be a false claim about what N counts.
 */
export function formatSampleCaption(
  sampleSize: number,
  phase: string = 'reached the plan phase',
): string {
  return sampleSize === 1
    ? `Based on 1 case that ${phase}`
    : `Based on ${sampleSize} cases that ${phase}`
}

/**
 * A human list: "A", "A and B", "A, B and C".
 *
 * `empty` is what an empty list reads as: the caller knows the noun and this
 * does not, so a bare "" would leave a hole in the sentence.
 */
export function joinWithAnd(parts: Array<string>, empty = ''): string {
  if (parts.length === 0) return empty
  if (parts.length === 1) return parts[0]
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

const COUNT_FORMAT = new Intl.NumberFormat('en-US')

/** A whole-number count with thousands separators: `16096` reads `16,096`. */
export function formatCount(count: number): string {
  return COUNT_FORMAT.format(count)
}
